import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";

import api from "../../services/api";
import { usePagedList } from "../../hook/usePagedList";
import ClientGroupFilter from "../../components/ui/ClientGroupFilter";
import Pager from "../../components/ui/Pager";
import SeenPill, { unseen } from "../../components/ui/SeenPill";
import { relativeTime, exactTime } from "../../utils/time";
import { useSearchInput } from "../../hook/useSearchInput";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * The discussion register, filtered and paged on the server.
 *
 * Two screens show it. The Muhokamalar menu shows every client's threads, with
 * a client filter and a Mijoz column. A client's own Muhokamalar tab passes
 * `client`: the client is then fixed, the column is gone, and the group filter
 * lists that client's groups straight away. Both are the same component so the
 * two lists cannot drift apart.
 *
 * Narrowing by client is also what keeps this fast at millions of rows: with it
 * Postgres walks threads_by_client_recent and stops after a page.
 *
 * `from` travels to the thread page in the link's state, so its breadcrumb can
 * lead back to the client tab the thread was opened from.
 */
const ThreadsTable = ({ client = null }) => {
  const fixed = Boolean(client);
  const [scope, setScope] = useState({ client: client?.id || "", group: "" });
  const [status, setStatus] = useState("");
  const { input, setInput, search, setSearch } = useSearchInput();
  const [perPage, setPerPage] = useState(10);
  const [groups, setGroups] = useState([]);

  // ClientGroupFilter fetches groups only after a client is picked; a fixed
  // client is picked from the start, so its groups are loaded here.
  useEffect(() => {
    if (!fixed) return undefined;
    let live = true;
    api
      .get("/groups/", { params: { client: client.id, limit: 500 } })
      .then((res) => live && setGroups(res.data.results || []))
      .catch(() => live && setGroups([]));
    return () => {
      live = false;
    };
  }, [fixed, client?.id]);

  // `search` follows the box once typing pauses (useSearchInput): `q` is an
  // ILIKE over two text columns with no trigram index, so each request is a
  // scan of the register and one per keystroke would be too many.
  const params = useMemo(
    () => ({
      ...(scope.client ? { client: scope.client } : {}),
      ...(scope.group ? { group: scope.group } : {}),
      ...(status ? { status } : {}),
      ...(search ? { q: search } : {}),
    }),
    [scope.client, scope.group, status, search],
  );

  const { rows, count, loading, error, page, setPage, totalPages } = usePagedList(
    "/threads/",
    params,
    perPage,
  );

  const filtering = Boolean((!fixed && scope.client) || scope.group || status || search);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);
  const linkState = fixed ? { from: { clientId: client.id, clientName: client.name } } : undefined;

  const clear = () => {
    setScope({ client: fixed ? client.id : "", group: "" });
    setStatus("");
    setInput("");
    setSearch("");
  };

  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="Sarlavha yoki hujjat..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        {fixed ? (
          <select
            className="ms-select ms-select--sm"
            value={scope.group}
            aria-label="Guruh"
            onChange={(e) => setScope({ client: client.id, group: e.target.value })}
          >
            <option value="">Barcha guruhlar</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.parent_id ? `— ${g.name}` : g.name}
              </option>
            ))}
          </select>
        ) : (
          <ClientGroupFilter client={scope.client} group={scope.group} onChange={setScope} />
        )}

        <select
          className="ms-select ms-select--sm"
          value={status}
          aria-label="Holati"
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Koʻrinadiganlar</option>
          <option value="hidden">Yashirilganlar</option>
          <option value="all">Hammasi</option>
        </select>

        {filtering && (
          <button type="button" className="ms-btn ms-btn--ghost" onClick={clear}>
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />
      </form>

      {loading ? (
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:message-off" />
          <span>
            {filtering
              ? "Hech narsa topilmadi"
              : fixed
                ? "Bu tizimda hali muhokama yoʻq. U kimdir obyekt kartochkasini birinchi marta ochganda paydo boʻladi."
                : "Muhokamalar yoʻq"}
          </span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Sarlavha</th>
                  {!fixed && <th style={{ width: 170 }}>Mijoz tizimi</th>}
                  <th style={{ width: 190 }}>Obyekt</th>
                  <th style={{ width: 160 }}>Guruh</th>
                  <th style={{ width: 90 }}>Xabarlar</th>
                  <th style={{ width: 120 }}>Holati</th>
                  <th style={{ width: 140 }}>Oxirgi xabar</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className={unseen(t) ? "ms-row-unseen" : undefined}>
                    <td className="ms-td-strong ms-td-clamp">
                      <Link className="ms-link" to={`/muhokamalar/${t.id}`} state={linkState}>
                        {t.title || t.external_object_id}
                      </Link>
                      <SeenPill row={t} />
                    </td>
                    {!fixed && (
                      <td className="ms-td-clamp">
                        <Link className="ms-sublink" to={`/mijoz-tizimlari/${t.client_id}/muhokamalar`}>
                          {t.client_name}
                        </Link>
                      </td>
                    )}
                    <td>
                      <span className="ms-mono ms-ellipsis" title={t.external_object_id}>
                        {t.external_object_id}
                      </span>
                    </td>
                    <td className="ms-td-clamp">{t.group_name || "—"}</td>
                    <td className="ms-td-num">{t.message_count ?? 0}</td>
                    <td>
                      {t.is_hidden ? (
                        <span className="ms-badge ms-badge--danger">yashirilgan</span>
                      ) : (
                        <span className="ms-badge ms-badge--success">koʻrinadi</span>
                      )}
                    </td>
                    <td className="ms-td-muted" title={exactTime(t.last_message_at)}>
                      {t.last_message_at ? relativeTime(t.last_message_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>

            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select
                  className="ms-select ms-select--sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThreadsTable;
